import type {ComponentProps} from 'react';

import type {MetricsGrain} from './MetricsGrainControls';
import {MetricsTimeframeSelector} from './MetricsTimeframeSelector';
import type {MetricsScorecardControls as MetricsScorecardControlsState} from './useMetricsScorecardControls';
import {logWorkstreamMetricsTimeframeChanged} from './workstreamMetricAnalytics';

/**
 * Timeframe control for the Metrics view, rendered in the page toolbar (or the
 * scorecard's column header) so the Metrics scorecard needs no controls row.
 */
export const MetricsScorecardControls = ({
  controls,
  triggerSize,
  v4Analytics = false,
}: {
  controls: MetricsScorecardControlsState;
  triggerSize?: ComponentProps<typeof MetricsTimeframeSelector>['triggerSize'];
  /** Tag its timeframe events `v4: true` (/workstreams). */
  v4Analytics?: boolean;
}) => {
  const {effectiveYear, grain, quarter, setTimeframe} = controls;

  const handleSelect = ({
    grain: nextGrain,
    quarter: nextQuarter,
    year: nextYear,
  }: {
    grain: MetricsGrain;
    quarter: typeof quarter;
    year: number;
  }) => {
    if (nextGrain !== grain) {
      logWorkstreamMetricsTimeframeChanged('workstreams_scorecard', {
        change_type: 'grain',
        grain: nextGrain,
        quarter: nextGrain === 'quarterly' ? nextQuarter : undefined,
        year: nextYear,
        ...(v4Analytics ? {v4: true as const} : {}),
      });
    }

    const timeframeChanged =
      nextYear !== effectiveYear || (nextGrain === 'quarterly' && nextQuarter !== quarter);
    if (timeframeChanged) {
      logWorkstreamMetricsTimeframeChanged('workstreams_scorecard', {
        change_type: 'timeframe',
        grain: nextGrain,
        quarter: nextGrain === 'quarterly' ? nextQuarter : undefined,
        year: nextYear,
        ...(v4Analytics ? {v4: true as const} : {}),
      });
    }

    setTimeframe({grain: nextGrain, quarter: nextQuarter, year: nextYear});
  };

  return (
    <MetricsTimeframeSelector
      grain={grain}
      quarter={quarter}
      year={effectiveYear}
      onSelect={handleSelect}
      triggerSize={triggerSize}
    />
  );
};
